<script setup lang="ts">
import { computed, inject } from 'vue'
import AdvancedSettings from './AdvancedSettings.vue'
import BinaryReferencePicker from './BinaryReferencePicker.vue'
import MemberNameField from './MemberNameField.vue'
import { referenceOf } from './binary-reference'
import { memberSuggestions, referencedNode } from './member-names'
import type { BinaryReference, GroupServiceModel } from '@/types'
import { BBEL_SCOPE_KEY, type BbelScopeContext } from '@/views/builder/composables/bbel-context'

const props = defineProps<{
  modelValue: GroupServiceModel
}>()

const emit = defineEmits<{
  'update:modelValue': [value: GroupServiceModel]
}>()

function update(changes: Partial<GroupServiceModel>) {
  emit('update:modelValue', { ...props.modelValue, ...changes })
}

function updateSource(reference: BinaryReference) {
  update({ source: { $type: 'binary', reference } })
}

// The rows a Group reads are the elements of the array Source points at. Their fields, from the latest output
// sample, are what Key, Head and Fields suggest. With no sample the fields suggest nothing and take typed names.
const scopeCtx = inject<BbelScopeContext | undefined>(BBEL_SCOPE_KEY, undefined)
const sourceNode = computed(() => referencedNode(scopeCtx?.value.roots ?? [], referenceOf(props.modelValue.source)))
// Key is one member chain, so a field inside a nested object is offered. Head and Fields are names on the row.
const keySuggestions = computed(() => memberSuggestions(sourceNode.value, 'chains'))
const rowFieldSuggestions = computed(() => memberSuggestions(sourceNode.value, 'names'))

// Head and Fields sit under Advanced settings, whose badge counts the ones that are set.
const advancedAuthoredCount = computed(() => {
  const headIsSet = props.modelValue.head.length > 0
  const fieldsAreSet = props.modelValue.fields.length > 0
  return Number(headIsSet) + Number(fieldsAreSet)
})
</script>

<template>
  <div>
    <div class="mb-3">
      <div class="form-label">Source</div>
      <BinaryReferencePicker
        label="Source"
        :model-value="referenceOf(modelValue.source)"
        @update:model-value="updateSource"
      />
    </div>

    <div class="mb-3">
      <div class="form-label">Key</div>
      <MemberNameField
        label="Key"
        placeholder="Field name"
        :model-value="modelValue.key"
        :suggestions="keySuggestions"
        @update:model-value="update({ key: $event })"
      />
      <div class="form-text">
        Sort the source by this field. A key that comes back after its group has closed stops the run.
      </div>
    </div>

    <AdvancedSettings :authored-count="advancedAuthoredCount">
      <div class="mb-3">
        <div class="form-label">Head</div>
        <MemberNameField
          label="Head"
          placeholder="No head"
          multiple
          :model-value="modelValue.head"
          :suggestions="rowFieldSuggestions"
          @update:model-value="update({ head: $event })"
        />
        <div class="form-text">Fields that are the same on every row of a group, written once.</div>
      </div>
      <div class="mb-3">
        <div class="form-label">Fields</div>
        <MemberNameField
          label="Fields"
          placeholder="All fields"
          multiple
          :model-value="modelValue.fields"
          :suggestions="rowFieldSuggestions"
          @update:model-value="update({ fields: $event })"
        />
      </div>
    </AdvancedSettings>
  </div>
</template>
